/**
 * LeftShield Portal - Executive Risk Page
 * Route: /executive-risk | Nav: Intelligence > Executive Risk
 *
 * High-level risk overview with risk score, heatmap, top risks,
 * and vulnerability exposure metrics for executive stakeholders.
 *
 * The "Top Risks" table is migrated onto the List & Grid Standard — see
 * `.github/context/list-grid-standard.context.md` and
 * `.github/context/list-grid-standard-behaviours.context.md`, following the
 * reference migration in `src/pages/ApiKeysPage.tsx` (and the embedded-list
 * pattern in `src/pages/LearningPage.tsx`). The risk score card, heatmap,
 * vulnerability exposure tiles, and AI narrative are dashboard-style
 * cards/charts, not tabular lists, so they are left as-is. The table is
 * read-only (no per-row navigation or mutation in the source), so no
 * actions column was added.
 */
import React, { useEffect, useMemo } from 'react';
import {
  Title2,
  Title3,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  tokens,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
} from '@fluentui/react-components';
import {
  ShieldCheckmarkRegular,
  WarningRegular,
  BugRegular,
  ArrowTrendingRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi, vulnerabilitiesApi, findingsApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { UnauthenticatedBanner } from '../components/common/UnauthenticatedBanner';
import { PageHeader } from '../components/common/PageHeader';
import type { Finding, RiskHeatmapCell } from '../types';
import {
  ListToolbar,
  ListFrame,
  Pager,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';

// ---------------------------------------------------------------------------
// Custom styles for heatmap
// ---------------------------------------------------------------------------

const useLocalStyles = makeStyles({
  heatmapGrid: {
    display: 'grid',
    gridTemplateColumns: 'auto repeat(5, 1fr)',
    gridTemplateRows: 'repeat(5, 1fr) auto',
    gap: '2px',
    maxWidth: '480px',
  },
  heatmapCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '72px',
    height: '56px',
    borderRadius: tokens.borderRadiusSmall,
    fontWeight: 600,
    fontSize: tokens.fontSizeBase200,
    cursor: 'default',
  },
  heatmapLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontWeight: 500,
    padding: tokens.spacingVerticalXS,
  },
  riskScoreCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXL,
  },
  riskScoreValue: {
    fontSize: '56px',
    fontWeight: 700,
    lineHeight: 1,
  },
  narrativePlaceholder: {
    padding: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center' as const,
    fontStyle: 'italic',
  },
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getRiskLevelColor = (level?: string): string => {
  switch (level) {
    case 'Critical':
      return tokens.colorPaletteRedForeground1;
    case 'High':
      return tokens.colorPaletteRedForeground2;
    case 'Medium':
      return tokens.colorPaletteYellowForeground1;
    case 'Low':
      return tokens.colorPaletteGreenForeground1;
    default:
      return tokens.colorNeutralForeground1;
  }
};

const getRiskLevelBadgeColor = (level?: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (level) {
    case 'Critical':
    case 'High':
      return 'danger';
    case 'Medium':
      return 'warning';
    case 'Low':
      return 'success';
    default:
      return 'informative';
  }
};

const getSeverityColor = (severity: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (severity?.toLowerCase()) {
    case 'critical':
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'informative';
  }
};

const getHeatmapCellColor = (count: number): string => {
  if (count === 0) return tokens.colorNeutralBackground2;
  if (count <= 2) return tokens.colorPaletteGreenBackground2;
  if (count <= 5) return tokens.colorPaletteYellowBackground2;
  if (count <= 10) return tokens.colorPaletteDarkOrangeBackground1;
  return tokens.colorPaletteRedBackground2;
};

const getHeatmapCellTextColor = (count: number): string => {
  if (count === 0) return tokens.colorNeutralForeground3;
  if (count <= 2) return tokens.colorPaletteGreenForeground1;
  if (count <= 5) return tokens.colorPaletteYellowForeground1;
  if (count <= 10) return tokens.colorPaletteDarkOrangeForeground1;
  return tokens.colorPaletteRedForeground1;
};

// ---------------------------------------------------------------------------
// RiskHeatmap Component
// ---------------------------------------------------------------------------

const IMPACT_LABELS = ['Negligible', 'Minor', 'Moderate', 'Significant', 'Severe'];
const LIKELIHOOD_LABELS = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'];

const RiskHeatmap: React.FC<{ cells: RiskHeatmapCell[] }> = ({ cells }) => {
  const ls = useLocalStyles();

  // Build a lookup: key = "likelihood-impact" -> count
  const lookup = useMemo(() => {
    const map: Record<string, number> = {};
    for (const cell of cells) {
      map[`${cell.likelihood}-${cell.impact}`] = cell.count;
    }
    return map;
  }, [cells]);

  // Render: rows from top (likelihood=5) to bottom (likelihood=1)
  const rows: React.ReactNode[] = [];
  for (let lik = 5; lik >= 1; lik--) {
    // Row label
    rows.push(
      <div key={`label-${lik}`} className={ls.heatmapLabel}>
        {LIKELIHOOD_LABELS[lik - 1]}
      </div>,
    );
    for (let imp = 1; imp <= 5; imp++) {
      const count = lookup[`${lik}-${imp}`] ?? 0;
      rows.push(
        <div
          key={`${lik}-${imp}`}
          className={ls.heatmapCell}
          style={{
            backgroundColor: getHeatmapCellColor(count),
            color: getHeatmapCellTextColor(count),
          }}
          title={`Likelihood: ${LIKELIHOOD_LABELS[lik - 1]}, Impact: ${IMPACT_LABELS[imp - 1]} - ${count} item(s)`}
        >
          {count > 0 ? count : ''}
        </div>,
      );
    }
  }

  // Bottom axis labels
  rows.push(<div key="corner" />);
  for (let imp = 1; imp <= 5; imp++) {
    rows.push(
      <div key={`imp-label-${imp}`} className={ls.heatmapLabel}>
        {IMPACT_LABELS[imp - 1]}
      </div>,
    );
  }

  return (
    <div>
      <div className={ls.heatmapGrid}>{rows}</div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: tokens.spacingVerticalS,
          maxWidth: '480px',
        }}
      >
        <Body2 style={{ color: tokens.colorNeutralForeground3, fontStyle: 'italic' }}>
          Likelihood (Y) vs Impact (X)
        </Body2>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Top Risks list — List & Grid Standard helpers
// ---------------------------------------------------------------------------

/** Severity has a fixed criticality order (Critical > High > Medium > Low > Info), not an
 *  alphabetical one — `getRiskSortValue` remaps it to a numeric rank so `severity:asc` sorts
 *  Critical-first, matching the original "Top 10 highest-severity findings" behaviour. */
const SEVERITY_SORT_RANK: Record<string, number> = { Critical: 0, High: 1, Medium: 2, Low: 3, Info: 4 };

function getRiskSortValue(item: Finding, field: string): unknown {
  if (field === 'severity') return SEVERITY_SORT_RANK[item.severity] ?? 9;
  return (item as unknown as Record<string, unknown>)[field];
}

/** `findingsApi.getAll` already returns a server-paginated `PaginatedResponse<Finding>`, which is
 *  structurally identical to `PagedResult<Finding>` — no unpaged-wrapper needed here (contrast
 *  `fetchApiKeysPage`/`fetchControlsPage` in the reference migrations, which wrap unpaged endpoints). */
async function fetchTopRisksPage(params: ListQueryParams): Promise<PagedResult<Finding>> {
  return findingsApi.getAll({}, params.page, params.pageSize);
}

// ---------------------------------------------------------------------------
// ExecutiveRiskPage
// ---------------------------------------------------------------------------

export const ExecutiveRiskPage: React.FC = () => {
  const ps = usePageStyles();
  const ls = useLocalStyles();

  // Fetch data
  const { data: secDashboard, isLoading: secLoading, isError, error } = useQuery({
    queryKey: ['securityDashboard'],
    queryFn: () => dashboardApi.getSecurityDashboard(),
  });

  const { isLoading: overviewLoading } = useQuery({
    queryKey: ['dashboardOverview'],
    queryFn: () => dashboardApi.getDashboardOverview(),
  });

  const { data: vulnsData, isLoading: vulnsLoading } = useQuery({
    queryKey: ['allVulnerabilities'],
    queryFn: () => vulnerabilitiesApi.getAll({}, 1, 100),
  });

  const { data: findingsData, isLoading: findingsLoading } = useQuery({
    queryKey: ['allFindings'],
    queryFn: () => findingsApi.getAll({}, 1, 100),
  });

  const isLoading = secLoading || overviewLoading;

  // Build heatmap cells from findings severity distribution
  const heatmapCells: RiskHeatmapCell[] = useMemo(() => {
    const cells: RiskHeatmapCell[] = [];
    const findings = findingsData?.items ?? [];

    // Map severity to impact: Critical=5, High=4, Medium=3, Low=2, Info=1
    const severityToImpact = (sev: string): number => {
      switch (sev) {
        case 'Critical': return 5;
        case 'High': return 4;
        case 'Medium': return 3;
        case 'Low': return 2;
        default: return 1;
      }
    };

    // Map status to rough likelihood: Open=5, InProgress=3, Resolved=1, Suppressed=2
    const statusToLikelihood = (status: string): number => {
      switch (status) {
        case 'Open': return 5;
        case 'InProgress': return 3;
        case 'Suppressed': return 2;
        case 'FalsePositive': return 1;
        case 'Resolved':
        case 'Closed': return 1;
        default: return 3;
      }
    };

    // Aggregate
    const counts: Record<string, { count: number; ids: string[] }> = {};
    for (const f of findings) {
      const lik = statusToLikelihood(f.status);
      const imp = severityToImpact(f.severity);
      const key = `${lik}-${imp}`;
      if (!counts[key]) counts[key] = { count: 0, ids: [] };
      counts[key].count++;
      counts[key].ids.push(f.id);
    }

    // Fill entire 5x5 grid
    for (let lik = 1; lik <= 5; lik++) {
      for (let imp = 1; imp <= 5; imp++) {
        const key = `${lik}-${imp}`;
        const entry = counts[key] ?? { count: 0, ids: [] };
        cells.push({
          likelihood: lik,
          impact: imp,
          count: entry.count,
          findings: entry.ids,
        });
      }
    }
    return cells;
  }, [findingsData]);

  // Top Risks list — List & Grid Standard (page/sort/search persisted to the URL)
  const { state, setPage, setSort, setSearch } = useListUrlState({ defaultSort: 'severity:asc' });

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const risksGrid = useAdaptiveGridData<Finding>({
    queryKeyBase: ['executiveTopRisks'],
    fetchPage: fetchTopRisksPage,
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['title', 'frameworkCode'],
    sortState,
    getValue: getRiskSortValue,
  });

  const {
    items: riskFindings,
    totalCount: risksTotalCount,
    totalPages: risksTotalPages,
    hasPreviousPage: risksHasPreviousPage,
    hasNextPage: risksHasNextPage,
    isLoading: risksLoading,
    isFetching: risksFetching,
    isError: risksIsError,
    refetch: refetchRisks,
  } = risksGrid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after search/sort changes.
  useEffect(() => {
    if (!risksLoading && state.page > risksTotalPages) {
      setPage(risksTotalPages);
    }
  }, [risksLoading, state.page, risksTotalPages, setPage]);

  // Vulnerability exposure stats
  const vulnStats = useMemo(() => {
    const items = vulnsData?.items ?? [];
    const kevCount = items.filter((v) => v.isKnownExploited).length;
    const epssTopCount = items.filter((v) => (v.epssPercentile ?? 0) >= 0.9).length;
    return { kevCount, epssTopCount, total: items.length };
  }, [vulnsData]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
        <Spinner size="large" label="Loading executive risk data..." />
      </div>
    );
  }

  const queryError = error as any;
  const isUnauth = queryError?.status === 401 || queryError?.message?.includes('MSAL');
  if (isError && isUnauth) {
    return (
      <div className={ps.page}>
        <PageHeader title="Executive Risk Overview" subtitle="Where is governance risk concentrated right now? Real-time risk heatmap and exposure analysis." />
        <UnauthenticatedBanner feature="risk intelligence" />
      </div>
    );
  }

  let riskListBody: React.ReactNode;
  if (risksLoading) {
    riskListBody = <LoadingSkeleton rows={6} columns={4} />;
  } else if (risksIsError) {
    riskListBody = <ErrorState message="Unable to load risk findings." onRetry={refetchRisks} />;
  } else if (riskFindings.length === 0) {
    riskListBody = (
      <EmptyState
        icon={<ShieldCheckmarkRegular />}
        title="No Findings Detected"
        description="Your risk posture is clear."
      />
    );
  } else {
    riskListBody = (
      <ListLoadingOverlay busy={risksFetching && !risksLoading}>
        <Table aria-label="Top risks">
          <TableHeader>
            <TableRow>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'severity' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('severity')}
                onClick={() => toggleSort('severity')}
              >
                Severity
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'title' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('title')}
                onClick={() => toggleSort('title')}
                style={{ width: '100%' }}
              >
                Title
              </TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Framework</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {riskFindings.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Badge appearance="filled" color={getSeverityColor(item.severity)}>
                    {item.severity}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Body1 style={{ fontWeight: 500 }}>{item.title}</Body1>
                </TableCell>
                <TableCell>
                  <Badge
                    appearance="tint"
                    color={item.status === 'Open' ? 'danger' : item.status === 'InProgress' ? 'warning' : 'success'}
                  >
                    {item.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge appearance="outline">{item.frameworkCode || '-'}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  const riskScore = secDashboard?.riskScore ?? 0;
  const riskLevel = secDashboard?.riskLevel || 'None';
  const trend24h = secDashboard?.scoreChange24h ?? 0;

  return (
    <div className={ps.page}>
      <PageHeader title="Executive Risk Overview" subtitle="Where is governance risk concentrated right now? Real-time risk heatmap and exposure analysis." />

      {/* Risk Score Card */}
      <Card>
        <div className={ls.riskScoreCard}>
          <Body2 style={{ color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Overall Risk Score
          </Body2>
          <div className={ls.riskScoreValue} style={{ color: getRiskLevelColor(riskLevel) }}>
            {riskScore}
          </div>
          <Badge
            appearance="filled"
            color={getRiskLevelBadgeColor(riskLevel)}
            size="large"
          >
            {riskLevel === 'None' ? 'No active risk signals detected' : `${riskLevel} Risk`}
          </Badge>
          <Body2 style={{ color: trend24h >= 0 ? tokens.colorPaletteRedForeground1 : tokens.colorPaletteGreenForeground1 }}>
            {trend24h >= 0 ? '+' : ''}{trend24h} pts in 24h
          </Body2>
        </div>
      </Card>

      {/* Summary Stats Row */}
      <div className={ps.metricGrid}>
        <Card>
          <CardHeader
            image={<ShieldCheckmarkRegular style={{ fontSize: '1.5rem', color: getRiskLevelColor(riskLevel) }} />}
            header={<Body2>Total Risk Score</Body2>}
            description={<Title2 style={{ color: getRiskLevelColor(riskLevel) }}>{riskScore}</Title2>}
          />
        </Card>
        <Card>
          <CardHeader
            image={<WarningRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteRedForeground1 }} />}
            header={<Body2>Critical Count</Body2>}
            description={
              <Title2 style={{ color: tokens.colorPaletteRedForeground1 }}>
                {secDashboard?.criticalFindings ?? 0}
              </Title2>
            }
          />
        </Card>
        <Card>
          <CardHeader
            image={<BugRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteRedForeground2 }} />}
            header={<Body2>Open Findings</Body2>}
            description={
              <Title2>{secDashboard?.openFindings ?? 0}</Title2>
            }
          />
        </Card>
        <Card>
          <CardHeader
            image={<ArrowTrendingRegular style={{ fontSize: '1.5rem', color: trend24h >= 0 ? tokens.colorPaletteRedForeground1 : tokens.colorPaletteGreenForeground1 }} />}
            header={<Body2>24h Trend</Body2>}
            description={
              <Title2
                style={{
                  color: trend24h >= 0
                    ? tokens.colorPaletteRedForeground1
                    : tokens.colorPaletteGreenForeground1,
                }}
              >
                {trend24h >= 0 ? '+' : ''}{trend24h}
              </Title2>
            }
          />
        </Card>
      </div>

      {/* Two-column layout: Heatmap and Vulnerability Exposure */}
      <div className={ps.twoColumn}>
        {/* Risk Heatmap */}
        <Card>
          <CardHeader header={<Title2>Risk Heatmap</Title2>} />
          <div className={ps.cardBody}>
            {findingsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXL }}>
                <Spinner size="medium" label="Building heatmap..." />
              </div>
            ) : (
              <RiskHeatmap cells={heatmapCells} />
            )}
          </div>
        </Card>

        {/* Vulnerability Exposure Card */}
        <Card>
          <CardHeader header={<Title2>Vulnerability Exposure</Title2>} />
          <div className={ps.cardBody}>
            {vulnsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXL }}>
                <Spinner size="medium" label="Loading vulnerability data..." />
              </div>
            ) : (
              <div className={ps.statGrid}>
                <div className={ps.statTile}>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>KEV Count</Body2>
                  <Title2 style={{ color: tokens.colorPaletteRedForeground1 }}>{vulnStats.kevCount}</Title2>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Known Exploited</Body2>
                </div>
                <div className={ps.statTile}>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>EPSS Top 10%</Body2>
                  <Title2 style={{ color: tokens.colorPaletteRedForeground2 }}>{vulnStats.epssTopCount}</Title2>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>High Exploit Probability</Body2>
                </div>
                <div className={ps.statTile}>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Total Vulnerabilities</Body2>
                  <Title2>{vulnStats.total}</Title2>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Tracked</Body2>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Top Risks — List & Grid Standard (embedded list within the executive dashboard) */}
      <Card>
        <CardHeader header={<Title2>Top Risks</Title2>} description={<Body2>Highest severity findings requiring attention</Body2>} />
        <div className={ps.cardBody}>
          <ListToolbar
            search={state.search}
            onSearchChange={setSearch}
            searchPlaceholder="Search risks…"
            onRefresh={refetchRisks}
            isRefreshing={risksFetching}
            testIdPrefix="executive-risks"
          />
          <ListFrame>{riskListBody}</ListFrame>
          <Pager
            page={state.page}
            totalPages={risksTotalPages}
            hasPreviousPage={risksHasPreviousPage}
            hasNextPage={risksHasNextPage}
            totalCount={risksTotalCount}
            onPageChange={setPage}
            testId="executive-risks-pager"
          />
        </div>
      </Card>

      {/* AI Executive Narrative */}
      <Card>
        <CardHeader header={<Title2>AI Executive Narrative</Title2>} />
        <div className={ps.cardBody}>
          <div className={ls.narrativePlaceholder}>
            <Title3 style={{ color: tokens.colorNeutralForeground3 }}>AI Executive Narrative</Title3>
            <Body1 style={{ color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalS }}>
              Configure the AI Narrator to generate executive-level risk summaries. Connect your AI integration in Settings to enable AI-powered insights.
            </Body1>
          </div>
        </div>
      </Card>
    </div>
  );
};
